import {
  addDaysToDate,
  convertDateToStringYYYYMMDD,
  getDateFromValue,
  parseDateOnlyStringYYYYMMDD,
} from "../../../utils/date";
import { jsonReviver } from "../../../utils/json";
import { camelCaseToSentenceCase } from "../../../utils/string";
import type { WorkflowTemplatePhaseItem } from "../../../utils/workflow";
import { getWorkflowStageKey, getWorkflowStageKeys, normalizeStage } from "../../../utils/workflow";
import { DealStageDateSection, DealStageDatesRecord } from "../models/EditDealDatesPanel";

type StageRecord = Record<string, unknown> | null | undefined;

const STAGE_TITLE_KEYS = ["title", "label", "name"];
const STAGE_DESCRIPTION_KEYS = ["description", "detail", "subtitle"];
const STAGE_START_KEYS = ["start", "startDate"];
const STAGE_END_KEYS = ["end", "endDate"];
const STAGE_STATUS_KEYS = ["status"];
const STAGE_RESERVED_KEYS = new Set(
  [...STAGE_TITLE_KEYS, ...STAGE_DESCRIPTION_KEYS, ...STAGE_START_KEYS, ...STAGE_END_KEYS, ...STAGE_STATUS_KEYS].map(
    normalizeStage
  )
);

const getExistingStageKey = (stage: StageRecord, keys: string[]): string | undefined => {
  if (!stage) {
    return undefined;
  }

  const normalizedKeys = new Set(keys.map(normalizeStage));
  return Object.keys(stage).find((key) => normalizedKeys.has(normalizeStage(key)));
};

const getStageField = <T>(stage: StageRecord, keys: string[]): T | undefined => {
  const matchingKey = getExistingStageKey(stage, keys);
  if (!matchingKey) {
    return undefined;
  }

  return (stage as Record<string, unknown>)[matchingKey] as T | undefined;
};

const setStageField = (stage: StageRecord, keys: string[], value: string | null): Record<string, unknown> => {
  const nextStage = { ...(stage ?? {}) };
  const matchingKey = getExistingStageKey(nextStage, keys) ?? keys[0];
  nextStage[matchingKey] = value;
  return nextStage;
};

export const parseDealDates = (dealDates: unknown): DealStageDatesRecord => {
  if (!dealDates) {
    return {};
  }

  if (typeof dealDates === "string") {
    try {
      return JSON.parse(dealDates, jsonReviver) as DealStageDatesRecord;
    } catch {
      return {};
    }
  }

  if (typeof dealDates === "object") {
    return dealDates as DealStageDatesRecord;
  }

  return {};
};

export const mapDealDatesToSections = (
  dealDates: DealStageDatesRecord,
  workflowTemplatePhases: WorkflowTemplatePhaseItem[] = []
): DealStageDateSection[] =>
  getWorkflowStageKeys(Object.keys(dealDates), workflowTemplatePhases).map((key) => {
    const value = dealDates[key];
    const matchingWorkflowTemplatePhase = workflowTemplatePhases.find(
      (phaseItem) => getWorkflowStageKey(phaseItem.phase) === key
    );
    const keyDates = (matchingWorkflowTemplatePhase?.keyDates ?? []).filter(
      (keyDate) => !STAGE_RESERVED_KEYS.has(normalizeStage(keyDate))
    );
    const additionalKeyDates = Object.entries(value ?? {})
      .filter(
        ([fieldKey, fieldValue]) =>
          !STAGE_RESERVED_KEYS.has(normalizeStage(fieldKey)) &&
          !keyDates.some((keyDate) => normalizeStage(keyDate) === normalizeStage(fieldKey)) &&
          getDateFromValue(fieldValue) !== undefined
      )
      .map(([fieldKey, fieldValue]) => ({
        key: fieldKey,
        label: camelCaseToSentenceCase(fieldKey),
        value: getDateFromValue(fieldValue),
      }));

    return {
      key,
      title:
        getStageField<string>(value, STAGE_TITLE_KEYS) ??
        camelCaseToSentenceCase(matchingWorkflowTemplatePhase?.phase ?? key),
      description: getStageField<string>(value, STAGE_DESCRIPTION_KEYS),
      start: getDateFromValue(getStageField(value, STAGE_START_KEYS)),
      end: getDateFromValue(getStageField(value, STAGE_END_KEYS)),
      keyDates: [
        ...keyDates.map((keyDate) => ({
          key: getExistingStageKey(value, [keyDate]) ?? keyDate,
          label: camelCaseToSentenceCase(keyDate),
          value: getDateFromValue(getStageField(value, [keyDate])),
        })),
        ...additionalKeyDates,
      ],
    };
  });

export const updateDealDateSection = (
  dealDates: DealStageDatesRecord,
  stageKey: string,
  field: string,
  value: string | null,
  workflowTemplatePhases: WorkflowTemplatePhaseItem[] = []
): DealStageDatesRecord => {
  const normalizedField = normalizeStage(field);
  let targetKeys;

  if (normalizedField === normalizeStage(STAGE_START_KEYS[0])) targetKeys = STAGE_START_KEYS;
  else if (normalizedField === normalizeStage(STAGE_END_KEYS[0])) targetKeys = STAGE_END_KEYS;
  else targetKeys = [field];

  const nextDealDates: DealStageDatesRecord = {
    ...dealDates,
    [stageKey]: setStageField(dealDates[stageKey], targetKeys, value),
  };

  if (normalizedField !== normalizeStage(STAGE_END_KEYS[0])) {
    return nextDealDates;
  }

  const orderedStageKeys = getWorkflowStageKeys(Object.keys(dealDates), workflowTemplatePhases);
  const currentStageIndex = orderedStageKeys.findIndex((key) => key === stageKey);
  const nextStageKey = currentStageIndex >= 0 ? orderedStageKeys[currentStageIndex + 1] : undefined;

  if (!nextStageKey) {
    return nextDealDates;
  }
  const nextStageStartDate = value ? (parseDateOnlyStringYYYYMMDD(value) ?? getDateFromValue(value)) : undefined;
  const nextStageStartValue = nextStageStartDate
    ? convertDateToStringYYYYMMDD(addDaysToDate(nextStageStartDate, 1))
    : null;

  return {
    ...nextDealDates,
    [nextStageKey]: setStageField(nextDealDates[nextStageKey], STAGE_START_KEYS, nextStageStartValue),
  };
};
